import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CONSENT_KEY, currentConsent, readConsent, setConsent, trackPage, trackBuyActivation } from '../lib/metaPixel';
import './marketing-consent.css';

export default function MarketingConsent() {
  const { pathname } = useLocation();
  const [choice, updateChoice] = useState(currentConsent);
  const [open, setOpen] = useState(() => !currentConsent());
  const opener = useRef(null);
  const heading = useRef(null);

  useEffect(() => { trackPage(pathname); }, [pathname, choice]);
  useEffect(() => {
    document.addEventListener('click', trackBuyActivation);
    document.addEventListener('auxclick', trackBuyActivation);
    const sync = (event) => {
      if (event.key !== CONSENT_KEY && event.key !== null) return;
      const value = readConsent();
      setConsent(value, false);
      updateChoice(value);
      setOpen(!value);
    };
    const openSettings = () => {
      opener.current = document.activeElement;
      setOpen(true);
      requestAnimationFrame(() => heading.current?.focus());
    };
    window.addEventListener('open-marketing-privacy', openSettings);
    window.addEventListener('storage', sync);
    return () => {
      document.removeEventListener('click', trackBuyActivation);
      document.removeEventListener('auxclick', trackBuyActivation);
      window.removeEventListener('storage', sync);
      window.removeEventListener('open-marketing-privacy', openSettings);
    };
  }, []);

  function choose(value) {
    setConsent(value);
    updateChoice(value);
    setOpen(false);
    (opener.current?.isConnected ? opener.current : document.querySelector('header a'))?.focus({ preventScroll: true });
  }

  // Dismissing a first-time prompt means no tracking. Keep an existing choice
  // when closing settings, and never reopen automatically after a decision.
  function dismiss() { choose(choice || 'rejected'); }

  if (!open) return null;

  return (
    <aside className="marketing-consent" aria-label="Marketing privacy controls" onKeyDown={(event) => {
      if (event.key === 'Escape') { event.preventDefault(); dismiss(); }
    }}>
      <section id="marketing-panel" className="marketing-panel" aria-labelledby="marketing-title">
        <div className="marketing-copy">
          <h2 id="marketing-title" ref={heading} tabIndex={-1}>Optional marketing cookies</h2>
          <p>Allow Meta to measure page views and product clicks for advertising?</p>
          <details>
            <summary>Privacy details</summary>
            <p>Meta uses cookies and receives browser and connection information, page views,
              Logic Trainer views and clicks to Tindie. We send approved page addresses without
              query strings or fragments, and the product name and ID. We do not send contact-form
              details or enable advanced matching or server-side tracking. Checkout takes place on Tindie.</p>
            <p>Your choice applies to Meta tracking.
              Rejecting stops future Meta tracking and removes this site's Meta cookies.
              Your choice is saved in this browser where storage is available.
              Change it anytime using Marketing privacy in the footer.</p>
            <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Meta privacy policy</a>
          </details>
          {choice && <p className="marketing-status">Current choice: {choice === 'accepted' ? 'accepted' : 'rejected'}.</p>}
        </div>
        <div className="marketing-actions">
          <button onClick={() => choose('accepted')}>Accept marketing</button>
          <button onClick={() => choose('rejected')}>Reject marketing</button>
        </div>
        <button className="marketing-dismiss" aria-label={choice ? 'Close privacy settings' : 'Dismiss without accepting'}
          onClick={dismiss}>×</button>
      </section>
    </aside>
  );
}
